import React from 'react';

/**
 * Incapacidad médica de esta atención.
 *
 * La fecha de fin se calcula aquí y se envía al backend para que quede
 * persistida: un certificado ya entregado a un empleador no puede cambiar de
 * fechas porque después se corrigió la lógica de conteo.
 *
 * Mismo criterio que backend/src/services/pdf/sickLeaveDates.js: el día de
 * inicio cuenta como primer día, y más de 180 días deja de ser incapacidad
 * temporal y pasa a trámite de invalidez. Si cambia allá, cambia aquí.
 */
const MAX_DAYS = 180;
const LEAVE_TYPES = ['Enfermedad general', 'Accidente de trabajo', 'Accidente común'];

function endDateFor(startDate, days) {
  const n = Number(days);
  if (!startDate || !Number.isInteger(n) || n < 1 || n > MAX_DAYS) return '';
  const [y, m, d] = String(startDate).split('-').map(Number);
  if (!y || !m || !d) return '';
  // Fecha local y no new Date('2026-10-05'), que se interpreta en UTC y en
  // Colombia retrocede un día.
  const end = new Date(y, m - 1, d + (n - 1));
  const pad = (v) => String(v).padStart(2, '0');
  return `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;
}

function SickLeaveSection({ value, onChange }) {
  const excede = Number(value?.days) > MAX_DAYS;
  const endDate = endDateFor(value?.startDate, value?.days);

  // startDate y days siempre viajan junto con la endDate recalculada.
  const setDates = (field, fieldValue) => {
    const next = { ...value, [field]: fieldValue };
    onChange({ ...next, endDate: endDateFor(next.startDate, next.days) });
  };

  return (
    <div className="border-t pt-6">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!!value?.enabled}
          onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
          className="h-4 w-4"
        />
        <span className="text-lg font-semibold text-gray-900">Incluir incapacidad médica</span>
      </label>
      <p className="text-sm text-gray-500 ml-6 mb-4">
        Genera además un certificado independiente en PDF.
      </p>

      {value?.enabled && (
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label htmlFor="sick-start" className="block text-xs text-gray-500">Desde</label>
              <input
                id="sick-start"
                type="date"
                className="w-full border rounded px-3 py-2"
                value={value?.startDate || ''}
                onChange={(e) => setDates('startDate', e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="sick-days" className="block text-xs text-gray-500">Días</label>
              <input
                id="sick-days"
                type="number"
                min={1}
                max={MAX_DAYS}
                className="w-full border rounded px-3 py-2"
                value={value?.days || ''}
                onChange={(e) => setDates('days', e.target.value)}
              />
            </div>

            <div>
              <span className="block text-xs text-gray-500">Hasta</span>
              <p className="px-3 py-2 font-medium text-gray-900">{endDate || '—'}</p>
            </div>

            <div>
              <label htmlFor="sick-type" className="block text-xs text-gray-500">Tipo</label>
              <select
                id="sick-type"
                className="w-full border rounded px-3 py-2"
                value={value?.leaveType || ''}
                onChange={(e) => onChange({ ...value, leaveType: e.target.value })}
              >
                <option value="">—</option>
                {LEAVE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {excede && (
            <p className="text-sm text-red-600">
              Máximo {MAX_DAYS} días; por encima de eso corresponde trámite de invalidez.
            </p>
          )}

          <textarea
            className="w-full border rounded px-3 py-2"
            rows={2}
            placeholder="Recomendaciones"
            value={value?.recommendations || ''}
            onChange={(e) => onChange({ ...value, recommendations: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

export default SickLeaveSection;
